<script setup>
import {
  capitalized,
  formatNumberToString,
  formatStringToNumber,
} from "@/utilities/helpers";
import { typeEquipmentEnum } from "../core/FormData";
import { StatusEnum } from "@/utilities/enum";
</script>
<template>
  <div class="flex items-center justify-between">
    <h3 class="font-medium text-lg text-gray-800">
      Konsumsi Bahan Bakar (
      {{ existing.totalItems ?? 0 }}
      )
    </h3>
  </div>

  <hr class="border-t-2 border-dashed border-gray-500" />

  <div class="grid grid-cols-2 gap-3 items-center">
    <v-text-field
      color="#475569"
      variant="outlined"
      density="compact"
      hide-details
      clearable
      prepend-inner-icon="mdi-magnify"
      placeholder="Cari jenis bahan bakar"
      v-model="keyword"
      @keyup.enter="searchData"
    >
    </v-text-field>
    <div class="flex justify-end">
      <v-btn
        class="capitalize text-xs min-w-40 w-40 bg-gray-800"
        @click="addRow()"
        v-if="
          (!status ||
            status === StatusEnum.DRAFT ||
            status === StatusEnum.REVISION ||
            (status === StatusEnum.COMPLETED && isAdmin)) &&
          !isPjo
        "
      >
        <span class="text-white text-sm">Tambah List</span>
        <template v-slot:prepend>
          <img
            src="../../../assets/icon/add-square.png"
            alt="icon"
            class="w-5 h-auto"
            loading="lazy"
          />
        </template>
      </v-btn>
    </div>
  </div>
  <div class="w-full">
    <v-data-table-server
      :items-per-page="existing.perPage"
      :headers="
        localDataTable.headers.filter((item, index, array) => {
          if (
            (status === StatusEnum.COMPLETED && !isAdmin) ||
            status === StatusEnum.WAITING_APPROVAL
          ) {
            if (index === array.length - 1) {
              return item.title !== '';
            }
          }
          return true;
        })
      "
      :items="localDataTable.items"
      :loading="existing.loading"
      :items-length="existing.totalItems"
      :page="existing.page"
      @update:options="fetchData"
    >
      <template #thead>
        <thead class="summary">
          <tr class="h-14 border-y">
            <td></td>
            <td>
              <div
                class="w-full h-10 rounded-md bg-blue-100 flex justify-center items-center"
              >
                <h5 class="font-medium text-black text-sm">
                  {{ countTypeEquipment() }}
                </h5>
              </div>
            </td>
            <td>
              <div
                class="w-full h-10 rounded-md bg-blue-100 flex justify-center items-center"
              >
                <h5 class="font-medium text-black text-sm">
                  {{ countFuel() }}
                </h5>
              </div>
            </td>
            <td>
              <div
                class="w-full h-10 rounded-md bg-blue-100 flex justify-center items-center"
              >
                <h5 class="font-medium text-black text-sm">
                  {{ sumVolume() }}
                </h5>
              </div>
            </td>
            <td>
              <div
                class="w-full h-10 rounded-md bg-blue-100 flex justify-center items-center"
              >
                <h5 class="font-medium text-black text-sm">
                  {{ sumDistance() }}
                </h5>
              </div>
            </td>
            <td
              v-if="
                (!status ||
                  status === StatusEnum.DRAFT ||
                  status === StatusEnum.REVISION ||
                  (status === StatusEnum.COMPLETED && isAdmin)) &&
                !isPjo
              "
            ></td>
          </tr>
        </thead>
      </template>
      <template v-slot:item="{ item, index }">
        <tr class="border-gray-950 h-14 align-top" v-if="!item.deleted">
          <td class="pt-4 font-medium">
            <p>{{ getIndex(index)?.displayIndex }}</p>
          </td>
          <td class="py-2">
            <v-tooltip
              :text="item.type_equipment ?? null"
              location="top"
              :disabled="
                !item.type_equipment || item.type_equipment.trim() === ''
              "
              v-if="
                (!status ||
                  status === StatusEnum.DRAFT ||
                  status === StatusEnum.REVISION ||
                  (status === StatusEnum.COMPLETED && isAdmin)) &&
                !isPjo
              "
            >
              <template v-slot:activator="{ props }">
                <v-autocomplete
                  :key="item.uuid"
                  v-bind="item.type_equipment ? props : {}"
                  class="w-full"
                  variant="outlined"
                  color="#475569"
                  rounded="xs"
                  density="compact"
                  placeholder="Pilih Jenis Emisi"
                  :items="[
                    { title: 'Bergerak', value: typeEquipmentEnum.BERGERAK },
                    {
                      title: 'Tidak Bergerak',
                      value: typeEquipmentEnum.TIDAK_BERGERAK,
                    },
                  ]"
                  item-title="title"
                  item-value="value"
                  v-model="item.type_equipment"
                  :id="'type_equipment_' + getIndex(index)?.displayIndex"
                  :rules="formValidasi.type_equipment"
                  @update:model-value="
                    () => {
                      item.type_equipment === typeEquipmentEnum.TIDAK_BERGERAK
                        ? (item.distance = null)
                        : null;
                      item.changed = true;
                    }
                  "
                  autocomplete="off"
                  hide-details="auto"
                  @blur="item.type_equipment_touched = true"
                  :error="hasErrors(item, 'type_equipment')"
                  :error-messages="
                    existing.items.find((data) => data.uuid === item.uuid)
                      ?.duplicate
                      ? 'Jenis emisi tidak boleh duplicate.'
                      : getErrors(item, 'type_equipment')
                  "
                >
                </v-autocomplete>
              </template>
            </v-tooltip>
            <h6 v-else class="font-normal text-sm mt-2">
              {{ capitalized(item.type_equipment) }}
            </h6>
          </td>
          <td class="py-2">
            <v-tooltip
              :text="item.fuel?.name ?? null"
              location="top"
              :disabled="!item.fuel?.name || item.fuel?.name?.trim() === ''"
              v-if="
                (!status ||
                  status === StatusEnum.DRAFT ||
                  status === StatusEnum.REVISION ||
                  (status === StatusEnum.COMPLETED && isAdmin)) &&
                !isPjo
              "
            >
              <template v-slot:activator="{ props }">
                <v-autocomplete
                  :key="item.uuid"
                  v-bind="item.fuel?.name ? props : {}"
                  class="w-full"
                  variant="outlined"
                  color="#475569"
                  rounded="xs"
                  density="compact"
                  placeholder="Pilih Bahan Bakar"
                  :items="
                    listFuel.find((data) => data?.uuid === item?.uuid)?.value
                  "
                  item-title="title"
                  item-value="value"
                  v-model="item.fuel"
                  @input="
                    (event) => {
                      getFuel(event.target.value, item.uuid);
                      item.fuel = null;
                    }
                  "
                  :id="'fuel_' + getIndex(index)?.displayIndex"
                  :rules="formValidasi.fuel"
                  :error-messages="
                    existing.items.find((data) => data.uuid === item.uuid)
                      ?.duplicate
                      ? 'Bahan bakar tidak boleh duplicate.'
                      : getErrors(item, 'fuel')
                  "
                  autocomplete="off"
                  hide-details="auto"
                  @blur="item.fuel_touched = true"
                  @update:model-value="item.changed = true"
                  :error="hasErrors(item, 'fuel')"
                >
                </v-autocomplete>
              </template>
            </v-tooltip>
            <h6 v-else class="font-normal text-sm mt-2">
              {{ item.name }}
            </h6>
          </td>
          <td class="py-2">
            <v-text-field
              :key="item.uuid"
              variant="outlined"
              color="#475569"
              density="compact"
              placeholder="ex: 100"
              class="w-full"
              @input="handleInput($event, 'volume', index)"
              v-model="item.volume"
              :id="'volume_' + getIndex(index)?.displayIndex"
              :rules="formValidasi.volume"
              autocomplete="off"
              hide-details="auto"
              @blur="item.volume_touched = true"
              @update:model-value="item.changed = true"
              :error="hasErrors(item, 'volume')"
              :error-messages="getErrors(item, 'volume')"
              v-if="
                (!status ||
                  status === StatusEnum.DRAFT ||
                  status === StatusEnum.REVISION ||
                  (status === StatusEnum.COMPLETED && isAdmin)) &&
                !isPjo
              "
            >
            </v-text-field>
            <h6 v-else class="font-normal text-sm mt-2">
              {{ item.volume }}
            </h6>
          </td>
          <td class="py-2">
            <v-text-field
              :key="item.uuid"
              variant="outlined"
              color="#475569"
              density="compact"
              placeholder="ex: 100"
              class="w-full"
              :disabled="
                item.type_equipment === typeEquipmentEnum.TIDAK_BERGERAK
              "
              :bg-color="
                item.type_equipment === typeEquipmentEnum.TIDAK_BERGERAK
                  ? 'grey-lighten-3'
                  : ''
              "
              v-model="item.distance"
              :id="'distance_' + getIndex(index)?.displayIndex"
              :rules="
                item.type_equipment !== typeEquipmentEnum.TIDAK_BERGERAK
                  ? formValidasi.distance
                  : []
              "
              autocomplete="off"
              @input="handleInput($event, 'distance', index)"
              :hide-details="
                item.type_equipment !== typeEquipmentEnum.TIDAK_BERGERAK
                  ? 'auto'
                  : true
              "
              @blur="item.distance_touched = true"
              @update:model-value="item.changed = true"
              :error="hasErrors(item, 'distance')"
              :error-messages="getErrors(item, 'distance')"
              v-if="
                (!status ||
                  status === StatusEnum.DRAFT ||
                  status === StatusEnum.REVISION ||
                  (status === StatusEnum.COMPLETED && isAdmin)) &&
                !isPjo
              "
            >
            </v-text-field>
            <h6 v-else class="font-normal text-sm mt-2">
              {{ item.distance }}
            </h6>
          </td>
          <td
            class="text-center pt-4"
            v-if="
              (!status ||
                status === StatusEnum.DRAFT ||
                status === StatusEnum.REVISION ||
                (status === StatusEnum.COMPLETED && isAdmin)) &&
              !isPjo
            "
          >
            <img
              src="../../../assets/icon/trash.png"
              alt="icon"
              loading="lazy"
              class="min-w-6 w-6 cursor-pointer h-auto"
              @click="removeRow(item.existing_index)"
              :id="'remove_' + getIndex(index)?.displayIndex"
            />
          </td>
        </tr>
      </template>
    </v-data-table-server>
  </div>
</template>

<script>
export default {
  name: "ContentFuelComponent",
  data() {
    return {
      keyword: null,
    };
  },
  props: {
    dataTable: Object,
    existing: Object,
    listFuel: Array,
    handleInput: Function,
    addRow: Function,
    getFuel: Function,
    removeRow: Function,
    formValidasi: Object,
    workArea: String,
    isPjo: Boolean,
    status: String,
    isAdmin: Boolean,
  },
  computed: {
    localDataTable: {
      get() {
        return this.dataTable;
      },
      set(newValue) {
        this.$emit("update:dataTable", newValue); // Kirim event ke parent
      },
    },
    indexedData() {
      let counter = 1;
      let startIndex = this.dataTable.start_index ?? 0;
      return this.dataTable.items.map((dt, index) => {
        if (!dt.deleted) {
          return {
            ...dt,
            displayIndex: startIndex + counter++,
            realIndex: index,
          };
        } else {
          return { ...dt, displayIndex: null, realIndex: index };
        }
      });
    },
  },
  methods: {
    capitalized,
    async fetchData({ page, itemsPerPage } = {}) {
      this.$emit("fetch-data", page, itemsPerPage);
    },
    async searchData() {
      this.$emit("search-data", this.keyword);
    },
    getIndex(index) {
      return this.indexedData.find((item) => item.realIndex === index);
    },
    countTypeEquipment() {
      return new Set(
        this.existing.items
          .filter((item) => !item.deleted && item.type_equipment)
          .map((item) => item.type_equipment)
      ).size;
    },
    countFuel() {
      return new Set(
        this.existing.items
          .filter((item) => !item.deleted && item.fuel)
          .map((item) => item.fuel.id)
      ).size;
    },
    sumVolume() {
      let total = 0;
      total = this.existing.items
        .filter((item) => !item.deleted && item.volume)
        .map((data) => {
          const num = formatStringToNumber(data.volume);
          return isNaN(num) ? 0 : num;
        })
        .reduce((sum, num) => sum + num, 0);

      return formatNumberToString(total);
    },
    sumDistance() {
      let total = 0;
      total = this.existing.items
        .filter((item) => !item.deleted && item.distance)
        .map((data) => {
          const num = formatStringToNumber(data.distance);
          return isNaN(num) ? 0 : num;
        })
        .reduce((sum, num) => sum + num, 0);

      return formatNumberToString(total);
    },
    hasErrors(data, touched) {
      return this.getErrors(data, touched).length > 0;
    },
    getErrors(value, touched) {
      if (!value[`${touched}_touched`]) return [];
      const rules = this.formValidasi[touched];
      const errors = rules
        .map((rule) => rule(value[touched]))
        .filter((result) => result !== true);
      return errors;
    },
  },
  emits: ["fetch-data", "search-data"],
};
</script>

<style scoped>
:deep(.v-table > .v-table__wrapper > table > tbody > tr > td),
:deep(.v-table > .v-table__wrapper > table > thead > tr > td),
:deep(.v-table > .v-table__wrapper > table > thead > tr > th) {
  padding: 0px 5px;
}
</style>
